import { useMemo } from 'react';
import { isLearnerAccount, isTeacherAccount, isParentAccount, isAdminAccount, type AccountsState } from '../../../../accounts';
import { AppState } from '../../../../app/types';
import { APP_NAME } from '../../../../config/constants';
import { formatRelativeTime } from '../../../../utils/formatters';
import { formatGroupLabel } from '../../../../config/systemConfig';
import type { DirectoryAccount } from '../../../../data/supabase/adminDirectory';
import { getModuleLabel, resolveModuleCode } from '../../shared/moduleMetadata';
import { pseudonymFor } from '../../../shared/learnerPseudonym';
import type { UserRow, SchoolData, RoleFilter, FamilyMasteryEntry } from './types';
interface UseUsersDataParams {
  accounts: AccountsState | undefined;
  getLearnerData: ((learnerId: string) => AppState | null) | undefined;
  roleFilter: RoleFilter;
  searchQuery: string;
  schoolFilter: string;
  selectedUser: UserRow | null;
  /**
   * Break glass. Geeft `true` als deze beheerder NU een actief, gelogd
   * toegangsvenster heeft voor deze leerling.
   *
   * Dit is bewust hier ingebouwd en niet in de schermen: dit is het enige punt
   * waar `getLearnerData()` wordt aangeroepen. Elke weergave verderop — de
   * tabelkolommen, het detailpaneel, de mastery-grafiek, de CSV-export — leest
   * uit het resultaat van deze hook. Eén controle hier sluit ze dus allemaal
   * tegelijk, en er kan later geen scherm bijkomen dat de controle vergeet.
   *
   * Ontbreekt de functie, dan geldt "geen toegang". Fail closed.
   */
  hasDataAccess?: (learnerId: string) => boolean;
  /**
   * Accounts zoals de SERVER ze kent. Alles wat hier in staat maar niet in de
   * lokale store, wordt als extra rij aan de tabel toegevoegd.
   *
   * Zonder dit ziet een beheerder uitsluitend accounts die op zíjn apparaat
   * zijn aangemaakt — en dat is bijna nooit de vraag die hij stelt. Deze rijen
   * zijn bewust "mager": geen relaties, geen leerdata, geen bewerkformulier.
   * Ze beantwoorden alleen "dit account bestaat", en dat is precies wat de
   * server zonder break glass mag prijsgeven.
   */
  serverAccounts?: DirectoryAccount[];
}
export function useUsersData({
  accounts,
  getLearnerData,
  roleFilter,
  searchQuery,
  schoolFilter,
  selectedUser,
  hasDataAccess,
  serverAccounts
}: UseUsersDataParams) {
  // Build enriched user list
  const localUsers = useMemo((): UserRow[] => {
    if (!accounts || !accounts.accountsById) return [];
    const allAccounts = Object.values(accounts.accountsById);
    return allAccounts.map((account): UserRow => {
      let organization = '—';
      let linkedCount = 0;
      let xp = 0;
      let avgMastery = 0;
      let sessionCount = 0;
      let familyMastery: Record<string, FamilyMasteryEntry> = {};
      let groupName: string | undefined;
      if (isTeacherAccount(account)) {
        organization = account.schoolName || '—';
        linkedCount = (accounts.relationships.teacherToLearners[account.id] || []).length;
        // Derive classroom name from top-level registry
        const teacherClassrooms = Object.values(accounts.classrooms ?? {}).filter(c => c.teacherId === account.id);
        if (teacherClassrooms.length > 0) {
          groupName = teacherClassrooms.map(c => c.name).join(', ');
        }
      } else if (isParentAccount(account)) {
        linkedCount = (accounts.relationships.parentToLearners[account.id] || []).length;
      } else if (isLearnerAccount(account)) {
        const teacherId = Object.entries(accounts.relationships.teacherToLearners || {}).find(([_, ids]) => ids.includes(account.id))?.[0];
        const teacher = teacherId ? accounts.accountsById[teacherId] : null;
        organization = teacher && isTeacherAccount(teacher) ? teacher.schoolName || '—' : '—';
        const parentId = Object.entries(accounts.relationships.parentToLearners || {}).find(([_, ids]) => ids.includes(account.id))?.[0];
        linkedCount = parentId ? 1 : 0;
        // Alleen uitlezen met een actief toegangsvenster. Zonder grant blijven
        // xp, avgMastery, sessionCount en familyMastery op hun nulwaarde staan
        // en tonen de schermen daar een streepje. Het account zelf (naam, rol,
        // school, laatst actief) blijft wél zichtbaar: een beheerder moet
        // gebruikers kunnen beheren zonder in hun leerdata te kijken.
        if (getLearnerData && hasDataAccess?.(account.id)) {
          const data = getLearnerData(account.id);
          xp = data?.stats?.xp ?? 0;
          const rawMastery = data?.familyMastery || {};
          familyMastery = rawMastery as Record<string, FamilyMasteryEntry>;
          const masteryEntries = Object.values(familyMastery);
          const masteryValues = masteryEntries.map(m => m.masteryScore);
          avgMastery = masteryValues.length > 0 ? Math.round(masteryValues.reduce((sum, val) => sum + val, 0) / masteryValues.length) : 0;
          const totalAttempts = masteryEntries.reduce((sum, m) => sum + m.attempts, 0);
          sessionCount = Math.ceil(totalAttempts / 10);
        }
        groupName = formatGroupLabel(account.grade);
      } else if (isAdminAccount(account)) {
        organization = APP_NAME;
      }
      return {
        id: account.id,
        name: account.displayName,
        email: account.email || '—',
        role: account.role,
        organization,
        linkedCount,
        xp,
        lastActive: account.lastActive || 0,
        lastActiveFormatted: formatRelativeTime(account.lastActive, true),
        account,
        avgMastery,
        sessionCount,
        groupName,
        familyMastery
      };
    });
  }, [accounts, getLearnerData, hasDataAccess]);

  /**
   * De twee bronnen samenvoegen: lokaal eerst, serveraccounts erachteraan.
   *
   * Lokaal wint bij een id dat in beide voorkomt. Die rij is namelijk rijker
   * (relaties, klas, leerdata) en de servergegevens voegen daar niets aan toe
   * wat we niet al hebben. Andersom zou een volledige rij vervangen worden door
   * een magere.
   */
  const users = useMemo((): UserRow[] => {
    if (!serverAccounts || serverAccounts.length === 0) return localUsers;
    const known = new Set(localUsers.map(u => u.id));
    const extra = serverAccounts.filter(a => !known.has(a.id)).map((a): UserRow => {
      const lastActive = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
      return {
        id: a.id,
        name: a.label,
        email: a.email ?? '—',
        role: a.role,
        organization: '—',
        linkedCount: 0,
        xp: 0,
        lastActive,
        lastActiveFormatted: formatRelativeTime(lastActive, true),
        // Geen `account`: dit account staat niet in de lokale store. De
        // schermen die een `account` verwachten (bewerken, resetten) tonen
        // daardoor vanzelf niets in plaats van een half formulier dat
        // nergens naartoe schrijft.
        account: undefined,
        avgMastery: 0,
        sessionCount: 0,
        groupName: formatGroupLabel(a.grade),
        familyMastery: {},
        isServerOnly: true
      };
    });
    return [...localUsers, ...extra];
  }, [localUsers, serverAccounts]);

  // Build schools data for school view
  const schoolsData = useMemo((): SchoolData[] => {
    const schoolNames = Array.from(new Set(users.map(u => u.organization).filter(o => o !== '—'))).sort();
    return schoolNames.map(schoolName => {
      const schoolTeachers = users.filter(u => u.role === 'teacher' && u.organization === schoolName);
      const schoolLearners = users.filter(u => u.role === 'learner' && u.organization === schoolName);
      const avgMastery = schoolLearners.length > 0 ? Math.round(schoolLearners.reduce((sum, l) => sum + (l.avgMastery || 0), 0) / schoolLearners.length) : 0;
      return {
        name: schoolName,
        learnerCount: schoolLearners.length,
        teacherCount: schoolTeachers.length,
        avgMastery,
        learners: schoolLearners,
        teachers: schoolTeachers
      };
    });
  }, [users]);

  // Get unique schools for filter dropdown
  const allSchools = useMemo(() => {
    return Array.from(new Set(users.map(u => u.organization).filter(o => o !== '—'))).sort();
  }, [users]);

  // Counts
  const counts = useMemo(() => ({
    all: users.length,
    learner: users.filter(u => u.role === 'learner').length,
    teacher: users.filter(u => u.role === 'teacher').length,
    parent: users.filter(u => u.role === 'parent').length,
    admin: users.filter(u => u.role === 'admin').length,
    school: schoolsData.length
  }), [users, schoolsData]);

  // Filter users based on role and search
  const filtered = useMemo(() => {
    let result = users;
    if (roleFilter !== 'all' && roleFilter !== 'school') {
      result = result.filter(u => u.role === roleFilter);
    }
    if (schoolFilter && roleFilter === 'learner') {
      result = result.filter(u => u.organization === schoolFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      // Zoeken op SUPPORTNUMMER is het enige werkbare pad naar een leerling.
      //
      // Namen van leerlingen worden serverzijdig niet meegestuurd, dus zoeken op
      // naam levert voor server-only rijen niets op. Meldt een school "Klaasje
      // heeft een probleem", dan geeft de leerling (of de ouder) het nummer uit
      // zijn instellingen door en komt de beheerder hier op één rij uit — zonder
      // dat er een naam over de lijn hoeft.
      //
      // Het nummer wordt uit `u.id` afgeleid en staat nergens opgeslagen, dus dit
      // filter kan niet uit de pas lopen met wat de leerling ziet.
      result = result.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.organization.toLowerCase().includes(q) || u.role === 'learner' && pseudonymFor(u.id).toLowerCase().includes(q));
    }
    return result;
  }, [users, roleFilter, searchQuery, schoolFilter]);

  // Filter schools for school view
  const filteredSchools = useMemo(() => {
    if (!searchQuery) return schoolsData;
    const q = searchQuery.toLowerCase();
    return schoolsData.filter(s => s.name.toLowerCase().includes(q));
  }, [schoolsData, searchQuery]);

  // Mastery by subject for selected learner
  const selectedMastery = useMemo(() => {
    if (!selectedUser || selectedUser.role !== 'learner') return [];
    const fm = selectedUser.familyMastery || {};
    const bySubject: Record<string, number[]> = {};
    Object.values(fm).forEach(m => {
      const rawCode = m.familyId?.split('-')[0] ?? 'UNK';
      const subjectId = resolveModuleCode(rawCode);
      if (!bySubject[subjectId]) bySubject[subjectId] = [];
      bySubject[subjectId].push(m.masteryScore);
    });
    return Object.entries(bySubject).map(([subjectId, values]) => ({
      subjectId,
      label: getModuleLabel(subjectId),
      avg: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
      count: values.length
    })).sort((a, b) => b.avg - a.avg);
  }, [selectedUser]);
  return {
    users,
    schoolsData,
    allSchools,
    counts,
    filtered,
    filteredSchools,
    selectedMastery
  };
}